'use client'

import { ArrowLeft, ArrowRight } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useCallback, useEffect, useState } from 'react'

import { SectionHeading, type SectionHeadingLevel } from '@/components/home-editorial/common'
import { HOME_BI_SECTION_HEADING } from '@/components/home-editorial/home-bi-layout'
import type { DestaquePublicacao } from '@/components/home-editorial/types'
import { Button } from '@/components/ui/button'
import { Carousel, CarouselContent, CarouselItem, type CarouselApi } from '@/components/ui/carousel'
import { cn } from '@/utilities/ui'

import { MostReadSlide } from './MostReadSlide'

/** Peek responsivo do mock: ~1 slide mobile, ~2 em sm, 4 colunas em lg (sem gap; border-l separa). */
const ITEM_CLASS = 'min-w-0 shrink-0 grow-0 basis-[88%] !pl-0 sm:basis-[52%] lg:basis-1/4'

export type MostReadCarouselClientProps = {
  items: DestaquePublicacao[]
  sectionHeading?: string
  sectionHeadingLevel?: SectionHeadingLevel
  showSectionHeader?: boolean
  showMoreLink?: boolean
  moreLabel?: string
  moreHref?: string
  showThumbnail?: boolean
  contentTone?: 'auto' | 'light' | 'dark'
  headingId?: string
}

/**
 * Carrossel "Mais lidos" (cliente): mecânica Embla (igual `HomeXPostsBand` — setas só quando há
 * overflow, com cleanup dos listeners) + layout/peek fiéis ao mock shadcn. Os slides já chegam
 * renderizados pelo SSR (conteúdo crawlável); aqui só a navegação é hidratada.
 */
export function MostReadCarouselClient({
  items,
  sectionHeading,
  sectionHeadingLevel = 'h2',
  showSectionHeader = true,
  showMoreLink = true,
  moreLabel,
  moreHref,
  showThumbnail = true,
  contentTone = 'auto',
  headingId = 'most-read-carousel-heading',
}: MostReadCarouselClientProps) {
  const t = useTranslations('Home')
  const [api, setApi] = useState<CarouselApi>()
  const [showNav, setShowNav] = useState(false)

  const onSelect = useCallback((emblaApi: CarouselApi) => {
    if (!emblaApi) return
    setShowNav(emblaApi.canScrollPrev() || emblaApi.canScrollNext())
  }, [])

  useEffect(() => {
    if (!api) return
    onSelect(api)
    api.on('reInit', onSelect)
    api.on('select', onSelect)
    return () => {
      api.off('select', onSelect)
      api.off('reInit', onSelect)
    }
  }, [api, onSelect])

  if (!items || items.length === 0) return null

  const headingClass = cn(
    'min-w-0 pr-3',
    HOME_BI_SECTION_HEADING,
    contentTone === 'light' && 'text-white',
  )

  return (
    <section aria-labelledby={showSectionHeader ? headingId : undefined} className="min-w-0">
      {showSectionHeader && sectionHeading ? (
        <header className="mb-4 flex items-center justify-between gap-4 md:mb-5">
          <SectionHeading level={sectionHeadingLevel} id={headingId} className={headingClass}>
            {sectionHeading}
          </SectionHeading>
          {showMoreLink && moreHref ? (
            <a
              href={moreHref}
              className="text-muted-foreground hover:text-foreground inline-flex shrink-0 items-center gap-1 text-xs font-semibold tracking-wide uppercase transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
            >
              {moreLabel?.trim() || t('viewMore')}
            </a>
          ) : null}
        </header>
      ) : null}

      <div className={cn('relative', showNav && 'sm:px-10')}>
        <Carousel setApi={setApi} opts={{ align: 'start', loop: false }} className="w-full min-w-0">
          <CarouselContent className="-ml-0">
            {items.map((item, index) => (
              <CarouselItem key={item.href} className={ITEM_CLASS}>
                <MostReadSlide
                  item={item}
                  index={index}
                  showThumbnail={showThumbnail}
                  className="h-full"
                />
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
        {showNav ? (
          <>
            <Button
              type="button"
              variant="outline"
              size="icon"
              aria-label={t('prevPosts')}
              onClick={() => api?.scrollPrev()}
              className="absolute top-[38%] -left-1 size-9 -translate-y-1/2 rounded-full sm:left-0"
            >
              <ArrowLeft className="size-4" />
            </Button>
            <Button
              type="button"
              variant="outline"
              size="icon"
              aria-label={t('nextPosts')}
              onClick={() => api?.scrollNext()}
              className="absolute top-[38%] -right-1 size-9 -translate-y-1/2 rounded-full sm:right-0"
            >
              <ArrowRight className="size-4" />
            </Button>
          </>
        ) : null}
      </div>
    </section>
  )
}
